import {
  CurecallConfirmationModal,
  CurecallTimelineLine,
  CurecallTimelineMessageItem,
  useClickOutside,
} from '@honestica/curecall-ui'
import { TTimelineMessageElement } from './followup-timeline.type'
import { useTranslation } from 'react-i18next'
import { useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useLocalizedNavigate } from 'src/hooks/use-localized-navigate'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ProjectService } from 'src/services/project.service'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { useNotification } from 'src/providers/notification.provider'
import { useSession } from 'src/providers/auth.provider'
import { config } from 'src/config'
import { getErrorMessage, isConflictError } from 'src/utils/http.util'
import { getRelativeSendingDate } from '../messages/_components/programmed-message.helper'
import { twMerge } from 'src/utils/tw-merge.util'
import { TimelineMessageItemTooltip } from './timeline-message-item-tooltip.component'
import { TimelineMessageItemMenu } from './timeline-message-item-menu.component'
import { DuplicateProgrammedMessageModal } from '../messages/_components/duplicate-programmed-message-modal.component'

const TimelineMessageItem = ({
  timelineElement,
  correspondingMedEventName,
}: {
  timelineElement: TTimelineMessageElement
  correspondingMedEventName: string
}) => {
  const { t } = useTranslation()
  const { followupId } = useParams()
  const navigate = useLocalizedNavigate()
  const { project } = useCurrentProject()
  const { currentUser } = useSession()
  const { notify } = useNotification()
  const queryClient = useQueryClient()
  const containerRef = useRef<HTMLDivElement>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false)

  useClickOutside({ ref: containerRef, callBack: () => setIsMenuOpen(false) })

  const backofficeUrl =
    currentUser?.isAdmin && config.BACKOFFICE_URL
      ? `${config.BACKOFFICE_URL}/projects/${project.id}/workflows/${timelineElement.id}`
      : null

  const deleteMutation = useMutation({
    mutationFn: () => ProjectService.deleteSelfConfigurationMessage(project.id, timelineElement.id),
    onSuccess: () => {
      notify({
        title: t('pages.configuration.messages.success-title'),
        message: t('pages.configuration.messages.success-body'),
        appearance: 'SUCCESS',
      })
      queryClient.invalidateQueries({
        queryKey: ['followup-self-configuration', project.id, followupId],
      })
      setIsDeleteModalOpen(false)
    },
    onError: (error) => {
      notify({
        title: t('pages.configuration.messages.error-title'),
        message: isConflictError(error)
          ? t('pages.configuration.messages.not-deletable')
          : getErrorMessage(error),
        appearance: 'ERROR',
      })
      if (isConflictError(error)) {
        queryClient.invalidateQueries({
          queryKey: ['followup-self-configuration', project.id, followupId],
        })
      }
      setIsDeleteModalOpen(false)
    },
  })

  return (
    <>
      <div ref={containerRef} className={twMerge('c-relative c-max-w-full', !isMenuOpen && 'c-group')}>
        <CurecallTimelineMessageItem
          leftLabel={getRelativeSendingDate(timelineElement.schedule.durationDiff)}
          className="tw-relative c-cursor-pointer"
          wrapperClassName="tw-w-max tw-max-w-full"
          onClick={() => setIsMenuOpen((prev) => !prev)}
          onDoubleClick={
            backofficeUrl
              ? () => {
                  setIsMenuOpen(false)
                  window.open(backofficeUrl, '_blank', 'noopener,noreferrer')
                }
              : undefined
          }
        >
          {timelineElement.name}
        </CurecallTimelineMessageItem>

        <TimelineMessageItemTooltip
          timelineElement={timelineElement}
          correspondingMedEventName={correspondingMedEventName}
        />

        {isMenuOpen && (
          <TimelineMessageItemMenu
            isEditable={timelineElement.isEditable}
            notEditableReason={timelineElement.notEditableReason}
            onEdit={() => {
              navigate(`/${project.id}/configuration/followups/${followupId}/messages/${timelineElement.id}`)
              setIsMenuOpen(false)
            }}
            onDuplicate={() => {
              setIsDuplicateModalOpen(true)
              setIsMenuOpen(false)
            }}
            onDelete={() => {
              setIsDeleteModalOpen(true)
              setIsMenuOpen(false)
            }}
          />
        )}
      </div>
      <CurecallTimelineLine />

      <CurecallConfirmationModal
        title={t('pages.configuration.messages.confirm-delete')}
        isOpen={isDeleteModalOpen}
        onCancel={() => setIsDeleteModalOpen(false)}
        onConfirm={() => deleteMutation.mutate()}
        cancelButtonLabel={t('common.actions.cancel')}
        confirmButtonLabel={t('common.actions.delete')}
        color="ALERT"
      >
        <div className="c-text-base">{timelineElement.name}</div>
      </CurecallConfirmationModal>

      {isDuplicateModalOpen && (
        <DuplicateProgrammedMessageModal
          isOpen={isDuplicateModalOpen}
          onClose={() => setIsDuplicateModalOpen(false)}
          message={{ id: timelineElement.id, name: timelineElement.name, followupId: Number(followupId) }}
        />
      )}
    </>
  )
}

export { TimelineMessageItem }
